import Icon from "@components/Atoms/Icon";
import Skeleton from "@components/Atoms/ImageSkeleton";
import type { ClientCatalogProps } from "@core/SitePresenter/SitePresenter";
import type ApiUrlCreator from "@core-ui/ApiServices/ApiUrlCreator";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import ResourceService from "@core-ui/ContextServices/ResourceService/ResourceService";
import { CatalogStoreProvider } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import t from "@ext/localization/locale/translate";
import Alert from "@ext/markdown/elements/alert/render/component/Alert";
import DiagramData from "@ext/markdown/elements/diagrams/component/DiagramData";
import Drawio from "@ext/markdown/elements/drawio/render/component/Drawio";
import Fragment from "@ext/markdown/elements/fragment/render/components/Fragment";
import Html from "@ext/markdown/elements/html/render/components/HTML";
import InlineIcon from "@ext/markdown/elements/icon/render/components/Icon";
import ImageRenderer from "@ext/markdown/elements/image/render/components/ImageRenderer";
import InlineImage from "@ext/markdown/elements/inlineImage/render/components/InlineImage";
import RenderBlock from "@ext/markdown/elements/md/render/RenderBlock";
import { NoteType, noteIcons } from "@ext/markdown/elements/note/render/component/Note";
import OpenApi from "@ext/markdown/elements/openApi/render/OpenApi";
import type TabAttrs from "@ext/markdown/elements/tabs/model/TabAttrs";
import Tabs from "@ext/markdown/elements/tabs/render/component/Tabs";
import Video from "@ext/markdown/elements/video/render/components/Video";
import View from "@ext/markdown/elements/view/render/components/View";
import type { ReactNodeRenderers } from "@gaurussel/tiptap-diff-utility";
import type { Attrs } from "prosemirror-model";
import type { ReactElement, ReactNode } from "react";

interface OldArticleContext {
	apiUrlCreator: ApiUrlCreator;
	catalogProps: ClientCatalogProps;
}

interface ReactNodeRenderersOptions {
	oldContext?: OldArticleContext;
	isLoading?: boolean;
}

const DeletedNode = ({
	oldContext,
	isLoading,
	attrs,
	children,
}: {
	oldContext?: OldArticleContext;
	isLoading?: boolean;
	attrs: Attrs;
	children: ReactNode;
}): ReactElement => {
	if (isLoading) {
		return (
			<Skeleton height={attrs.height ?? "100%"} isLoaded={false} width={attrs.width ?? "100%"}>
				<div />
			</Skeleton>
		);
	}

	// Separate store: old and new file versions share a name in the catalog but differ in content.
	const node = <ResourceService.Provider>{children as ReactElement}</ResourceService.Provider>;

	if (!oldContext?.apiUrlCreator) return node;

	return (
		<ApiUrlCreatorService.Provider value={oldContext.apiUrlCreator}>
			<CatalogStoreProvider data={oldContext.catalogProps}>{node}</CatalogStoreProvider>
		</ApiUrlCreatorService.Provider>
	);
};

const DeletedCaption = ({ label, name }: { label: string; name?: string }): ReactElement => (
	<div className="font-medium">{name ? `${label}: ${name}` : label}</div>
);

const markTabs = (slot: HTMLElement | null) => {
	slot?.querySelectorAll<HTMLElement>(":scope tab-react-component").forEach((tab) => {
		tab.classList.add("tab");
		tab.dataset.tabIndex = tab.getAttribute("idx") ?? "";
	});
};

const DeletedTabs = ({ childAttrs, content }: { childAttrs: TabAttrs[]; content: ReactNode }) => (
	<Tabs childAttrs={childAttrs}>
		<div ref={markTabs}>{content}</div>
	</Tabs>
);

const text = (value: unknown): string => (typeof value === "string" ? value : "");

export const getReactNodeRenderers = ({
	oldContext,
	isLoading,
}: ReactNodeRenderersOptions = {}): ReactNodeRenderers => {
	return {
		image: (attrs: Attrs) => (
			<DeletedNode attrs={attrs} isLoading={isLoading} oldContext={oldContext}>
				<ImageRenderer
					alt={attrs.alt}
					crop={attrs.crop}
					height={attrs.height}
					id={attrs.id}
					objects={attrs.objects}
					realSrc={attrs.src}
					scale={attrs.scale}
					title={attrs.title}
					width={attrs.width}
				/>
			</DeletedNode>
		),
		inlineImage: (attrs: Attrs) => (
			<DeletedNode attrs={attrs} isLoading={isLoading} oldContext={oldContext}>
				<InlineImage
					alt={attrs.alt}
					height={attrs.height}
					renderSrc={attrs.renderSrc}
					src={attrs.src}
					width={attrs.width}
				/>
			</DeletedNode>
		),
		openapi: (attrs: Attrs) => (
			<DeletedNode attrs={attrs} isLoading={isLoading} oldContext={oldContext}>
				<OpenApi showSchemas={attrs.showSchemas ?? attrs.flag} src={attrs.src} />
			</DeletedNode>
		),
		drawio: (attrs: Attrs) => (
			<DeletedNode attrs={attrs} isLoading={isLoading} oldContext={oldContext}>
				<Drawio
					height={attrs.height}
					id={attrs.id}
					scale={attrs.scale}
					src={attrs.src}
					title={attrs.title}
					width={attrs.width}
				/>
			</DeletedNode>
		),
		video: (attrs: Attrs) => <Video inEditor path={text(attrs.path)} title={text(attrs.title)} />,
		note: (attrs: Attrs, content?: ReactNode) => {
			const type = (text(attrs.type) as NoteType) || NoteType.note;
			return (
				<div className={`admonition admonition-row admonition-${type}`} data-component="note">
					<div className="admonition-heading">
						<div className="admonition-icon">
							<Icon code={noteIcons[type] ?? noteIcons.note} strokeWidth="2" />
						</div>
					</div>
					<div className="admonition-content">{content}</div>
				</div>
			);
		},
		alert: (attrs: Attrs, content?: ReactNode) => (
			<Alert title={text(attrs.title)} type={text(attrs.type)}>
				{content}
			</Alert>
		),
		tabs: (attrs: Attrs, content?: ReactNode) => (
			<DeletedTabs childAttrs={Array.isArray(attrs.childAttrs) ? attrs.childAttrs : []} content={content} />
		),
		html: (attrs: Attrs) => <Html content={text(attrs.content)} inEditor />,
		icon: (attrs: Attrs) => (
			<InlineIcon
				code={text(attrs.code)}
				color={text(attrs.color) || undefined}
				svg={text(attrs.svg) || undefined}
			/>
		),
		view: (attrs: Attrs) => (
			<DeletedNode attrs={attrs} isLoading={isLoading} oldContext={oldContext}>
				<View
					defs={attrs.defs}
					display={attrs.display}
					groupby={attrs.groupby}
					orderby={attrs.orderby}
					select={attrs.select}
				/>
			</DeletedNode>
		),
		blockMd: (attrs: Attrs) => (
			<DeletedNode attrs={attrs} isLoading={isLoading} oldContext={oldContext}>
				<RenderBlock tag={attrs.tag} />
			</DeletedNode>
		),
		fragment: (attrs: Attrs) => (
			<DeletedNode attrs={attrs} isLoading={isLoading} oldContext={oldContext}>
				<>
					<DeletedCaption label={t("diff.deleted.fragment")} name={text(attrs.title) || text(attrs.id)} />
					{/* Fragment renders a "failed to load" error on empty content instead of nothing. */}
					{attrs.content && Object.keys(attrs.content).length > 0 && (
						<Fragment content={attrs.content} id={text(attrs.id)} />
					)}
				</>
			</DeletedNode>
		),
		diagrams: (attrs: Attrs) => (
			<DeletedNode attrs={attrs} isLoading={isLoading} oldContext={oldContext}>
				<DiagramData
					content={attrs.content}
					diagramName={attrs.diagramName}
					float={attrs.float}
					height={attrs.height}
					scale={attrs.scale}
					src={attrs.src}
					title={attrs.title}
					width={attrs.width}
				/>
			</DeletedNode>
		),
	};
};
